import { useState } from 'react';

export interface GalleryImage {
  readonly id: string;
  readonly url: string;
  readonly altText: string;
  readonly isCover: boolean;
  readonly displayOrder: number;
}

export interface ImageCardProps<TImage extends GalleryImage> {
  readonly image: TImage;
  readonly imageUrl: string;
  readonly onCover: () => void;
  readonly onDelete: () => void;
  readonly onUpdate: (altText: string) => void;
  readonly onDragStart: () => void;
  readonly onDragOver: (event: React.DragEvent<HTMLElement>) => void;
  readonly onDrop: () => void;
}

export function ImageCard<TImage extends GalleryImage>({
  image,
  imageUrl,
  onCover,
  onDelete,
  onUpdate,
  onDragStart,
  onDragOver,
  onDrop,
}: ImageCardProps<TImage>): React.JSX.Element {
  const [altText, setAltText] = useState(image.altText);

  return (
    <article
      className="space-y-3 rounded-lg border border-border bg-background p-3 shadow-sm"
      draggable
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
    >
      <div className="relative aspect-[4/3] overflow-hidden rounded-md bg-muted">
        <img className="h-full w-full object-cover" src={imageUrl} alt={image.altText} />
        <span className="absolute left-2 top-2 rounded bg-emerald-600 px-2 py-1 text-xs font-medium text-white">
          #{image.displayOrder + 1}
        </span>
        {image.isCover ? (
          <span className="absolute right-2 top-2 rounded bg-amber-500 px-2 py-1 text-xs font-medium text-white">
            Cover
          </span>
        ) : null}
        <span className="absolute bottom-2 left-1/2 -translate-x-1/2 rounded bg-black/70 px-2 py-1 text-xs text-white">
          Drag
        </span>
      </div>
      <label className="block text-xs font-medium text-muted-foreground">
        Alt text
        <input
          className="mt-1 w-full rounded border border-input bg-background px-2 py-1 text-sm text-foreground"
          value={altText}
          onChange={(event) => {
            setAltText(event.target.value);
          }}
          onBlur={() => {
            if (altText.trim() !== '' && altText !== image.altText) onUpdate(altText.trim());
          }}
        />
      </label>
      <div className="flex justify-end gap-2">
        {!image.isCover ? (
          <button className="rounded border px-2 py-1 text-xs" type="button" onClick={onCover}>
            Make cover
          </button>
        ) : null}
        <button
          className="rounded border border-destructive px-2 py-1 text-xs text-destructive"
          type="button"
          onClick={onDelete}
        >
          Delete
        </button>
      </div>
    </article>
  );
}
